import './barcode-subscriber.scss';

import { faBarcode, faCamera, faCircleNotch, faLightbulb, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import { first } from 'rxjs/operators';
import { Component, Watch } from 'vue-property-decorator';

import { ReactiveVue } from '@skinsoft/reactive-vue';

import { input$ } from '../keyboard';
import { CameraScanner, Reader } from './CameraScanner';

declare const CAMERA_SCANNER_ENABLE_ON_COMPATIBLE_DEVICE: boolean;
declare const CAMERA_SCANNER_READERS: Reader[];

@Component({
    props: ['width', 'height', 'embedded', 'eventFilterIdentifier'],
    template: require('./barcode-subscriber.html'),
})
export class BarcodeSubscriber extends ReactiveVue {
    public $refs: {
        innerWrapper: HTMLDivElement;
        video: HTMLVideoElement;
    };

    public eventFilterIdentifier: string;
    public width: number;
    public height: number;
    public embedded: boolean;

    private value: string = null as any;
    private hasSeveralDevices: boolean = false;
    private cameraReady: boolean = false;

    // Icons
    private lightBulbIcon = faLightbulb;
    private barcodeIcon = faBarcode;
    private cameraIcon = faCamera;
    private changeIcon = faSyncAlt;
    private loadingIcon = faCircleNotch;

    get supportsCamera() {
        return (
            CAMERA_SCANNER_ENABLE_ON_COMPATIBLE_DEVICE &&
            navigator &&
            navigator.mediaDevices &&
            navigator.mediaDevices.getUserMedia
        );
    }

    private cameraScanner: CameraScanner = null as any;

    private torchAvailable: boolean = false;

    public created() {
        this.cameraScanner = new CameraScanner(CAMERA_SCANNER_READERS,
            () => {
                this.cameraReady = true;
            },
            () => {
                this.cameraReady = false;
            },
            this.width, this.height);
    }

    public async mounted() {
        if (this.supportsCamera && CAMERA_SCANNER_ENABLE_ON_COMPATIBLE_DEVICE) {
            this.cameraScanner.pipe(first()).subscribe(async code => {
                code.split('').forEach(char => input$.next(
                    {
                        char,
                        inputEventIdentifier: this.eventFilterIdentifier,
                    }
                ));
                await this.close();
            });
            await this.cameraScanner.start();
            // Check if torch is available
            const videoStream = this.$refs.video?.srcObject;
            if (videoStream) {
                // @ts-ignore
                const capabilities = videoStream.getTracks()[0].getCapabilities();
                this.torchAvailable = !!capabilities.torch;
            }
            // check if the button to change camera has to be displayed
            this.hasSeveralDevices = this.cameraScanner.hasSeveralDevices;
        } else {
            this.subscribeToKeyPress();
        }
    }

    public async close() {
        if (this.supportsCamera) {
            await this.cameraScanner.stop();
            this.$emit('close');
        } else {
            this.unsubscribeFromKeyPress();
        }
    }

    public beforeDestroy() {
        this.unsubscribeFromKeyPress();
        this.cameraScanner.resetCallbacks()
        // if we don't stop it manually, camera is still in use in the background
        this.cameraScanner.stop()
    }

    public toggleTorch() {
        this.cameraScanner.toggleTorch();
    }

    public changeCamera() {
        this.cameraScanner.goToNextCameraDevice();
    }

    private subscribeToKeyPress() {
        window.addEventListener('keypress', this.onKeyPress);
    }

    private unsubscribeFromKeyPress() {
        window.removeEventListener('keypress', this.onKeyPress);
    }

    private onKeyPress = (event: KeyboardEvent) => {
        event.preventDefault();
        input$.next({ char: String.fromCharCode(event.which), inputEventIdentifier: this.eventFilterIdentifier });
    };
}
